"use client";

import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Layers } from "lucide-react";

import { OwnerBadge } from "@/components/site/sections/overview-board/owner-badge";
import { Reveal } from "@/components/site/sections/overview-board/reveal";
import {
  SECTION_STYLES,
  prefersReducedMotion,
} from "@/components/site/sections/overview-board/styles";
import {
  OVERVIEW_SECTIONS,
  folderTab,
  isViewerItem,
  toggleFolder,
  type FolderTab,
  type OverviewDocument,
  type OverviewInsight,
} from "@/lib/overview-document";
import type { OverviewSectionId } from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

// Below this deck width three tiles cannot hold "Opportunities", its count and
// the chevron in one row, so the tiles stack and cards open under their tile.
const STACK_BELOW = 760;

/**
 * Issues, opportunities and strengths as three tiles, all closed by default.
 * A tile opens its type as a folder: in a wide deck the tile becomes the front
 * tab of a panel below the tiles with the cards in one row; in a narrow deck
 * the tiles stack and the cards open under the tile. The open tile closes it.
 */
export function InsightDeck({
  document,
  headingLevel,
  onOpen,
}: {
  document: OverviewDocument;
  headingLevel: 2 | 3;
  onOpen: (id: string) => void;
}) {
  const deckRef = React.useRef<HTMLElement>(null);
  const panelId = React.useId();
  const narrow = useStackedDeck(deckRef);
  const [open, setOpen] = React.useState<OverviewSectionId | null>(null);
  // The panel keeps the last type while it closes, so its cards do not vanish mid-transition.
  const [shown, setShown] = React.useState<OverviewSectionId | null>(null);
  const Heading = `h${headingLevel}` as const;
  const CardHeading = `h${headingLevel + 1}` as "h3" | "h4";

  if (open !== null && open !== shown) {
    setShown(open);
  }

  return (
    <section ref={deckRef} className="@container flex flex-col">
      <Heading className="sr-only">Issues, opportunities and strengths</Heading>
      <div className="grid grid-cols-1 items-start gap-2.5 @min-[760px]:grid-cols-3 @min-[1000px]:grid-cols-[3fr_2fr_3fr]">
        {OVERVIEW_SECTIONS.map((section) => {
          const items = document.insights[section];
          const tab = folderTab(open, section, narrow);
          const stackDepth = Math.min(Math.max(items.length - 1, 0), 2);
          const bodyId = `${panelId}-${section}`;
          // Opening the folder tucks the stack under its tile instead of dropping it.
          const stackClassName = cn(
            "absolute rounded-[14px] border transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            open !== null && "-translate-y-2 opacity-0",
          );

          return (
            <div
              key={section}
              className={cn(
                "relative flex min-w-0 flex-col transition-[padding] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
                tab === "front" ? "z-[2]" : "z-[1]",
                open !== null
                  ? "pb-0"
                  : stackDepth === 2
                    ? "pb-2.5"
                    : stackDepth === 1
                      ? "pb-[5px]"
                      : "pb-0",
              )}
            >
              {stackDepth === 2 ? (
                <span
                  aria-hidden="true"
                  className={cn("inset-x-[18px] top-4 bottom-0 bg-muted/80", stackClassName)}
                />
              ) : null}
              {stackDepth >= 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "inset-x-[9px] top-2 bg-muted/40",
                    stackDepth === 2 ? "bottom-[5px]" : "bottom-0",
                    stackClassName,
                  )}
                />
              ) : null}
              <InsightTile
                section={section}
                count={items.length}
                tab={tab}
                controls={narrow ? bodyId : panelId}
                onPick={() => setOpen(toggleFolder(open, section))}
              />
              {narrow ? (
                <Reveal
                  id={bodyId}
                  open={open === section}
                  role="region"
                  aria-label={SECTION_STYLES[section].label}
                  className="relative z-[1] -mt-px"
                >
                  <div className="grid gap-2.5 rounded-b-[14px] border bg-muted p-2.5 sm:p-3 @min-[560px]:grid-cols-2">
                    {items.map((insight) => (
                      <InsightCard
                        key={insight.id}
                        insight={insight}
                        mine={isViewerItem(document, insight)}
                        heading={CardHeading}
                        onOpen={onOpen}
                      />
                    ))}
                  </div>
                </Reveal>
              ) : null}
            </div>
          );
        })}
      </div>

      {!narrow ? (
        <Reveal
          id={panelId}
          open={open !== null}
          role="region"
          aria-label={shown ? SECTION_STYLES[shown].label : undefined}
          className="relative z-[1] -mt-px"
        >
          {shown ? (
            <InsightStrip
              key={shown}
              items={document.insights[shown]}
              document={document}
              heading={CardHeading}
              onOpen={onOpen}
            />
          ) : null}
        </Reveal>
      ) : null}
    </section>
  );
}

function InsightTile({
  section,
  count,
  tab,
  controls,
  onPick,
}: {
  section: OverviewSectionId;
  count: number;
  tab: FolderTab;
  controls: string;
  onPick: () => void;
}) {
  const style = SECTION_STYLES[section];
  const Icon = style.icon;

  return (
    <button
      type="button"
      aria-expanded={tab === "front"}
      aria-controls={controls}
      onClick={onPick}
      className={cn(
        "group relative z-[2] flex h-[60px] w-full min-w-0 items-center gap-2.5 border pr-3 pl-3 text-left @min-[300px]:gap-3 transition-[border-radius,background-color,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none motion-reduce:transition-none",
        tab === "front" && "rounded-t-[14px] rounded-b-none border-b-transparent bg-muted",
        tab === "back" && "rounded-t-[14px] rounded-b-none bg-card hover:bg-muted/50",
        tab === "rest" &&
          "rounded-[14px] bg-card shadow-xs hover:border-foreground/35 hover:shadow-[0_10px_22px_-16px_rgba(0,0,0,0.4)]",
      )}
    >
      <span
        className={cn(
          "inline-flex size-8 flex-none items-center justify-center rounded-full border",
          style.badgeClassName,
        )}
      >
        <Icon className={cn("size-3.5", style.iconClassName)} aria-hidden="true" />
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-base leading-tight font-semibold tracking-[-0.012em] transition-colors duration-300",
          tab === "back" ? "text-foreground/65" : "text-foreground",
        )}
      >
        {style.label}
      </span>
      <span
        className={cn(
          "flex-none text-2xl leading-none font-semibold tracking-[-0.03em] tabular-nums transition-colors duration-300",
          tab === "back" ? "text-muted-foreground/70" : "text-foreground",
        )}
      >
        {count}
      </span>
      <span
        aria-hidden="true"
        className="inline-flex size-[30px] flex-none items-center justify-center rounded-full border border-foreground/20 bg-card text-foreground transition-colors duration-200 group-hover:border-foreground group-hover:bg-foreground group-hover:text-background"
      >
        <ChevronDown
          strokeWidth={2.2}
          className={cn(
            "size-[15px] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            tab === "front" && "rotate-180",
          )}
        />
      </span>
    </button>
  );
}

/**
 * One insight inside the open folder. The whole card opens the insight
 * window: the title button stretches over the card, so the card keeps its
 * heading and is a single tab stop.
 */
function InsightCard({
  insight,
  mine,
  heading: CardHeading,
  onOpen,
  className,
}: {
  insight: OverviewInsight;
  mine: boolean;
  heading: "h3" | "h4";
  onOpen: (id: string) => void;
  className?: string;
}) {
  return (
    <article
      className={cn(
        "relative flex min-w-0 flex-col gap-2.5 rounded-xl border bg-card px-4 py-3.5 transition-[border-color,box-shadow] duration-150 hover:border-foreground/25 hover:shadow-[0_10px_24px_-18px_rgba(15,23,42,0.35)] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
        className,
      )}
    >
      <CardHeading className="text-[0.9375rem] leading-[1.38] font-semibold tracking-[-0.01em] text-foreground">
        <button
          type="button"
          aria-haspopup="dialog"
          data-overview-item-trigger={insight.id}
          onClick={() => onOpen(insight.id)}
          className="text-left after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:outline-none"
        >
          {insight.title}
        </button>
      </CardHeading>
      <p className="line-clamp-2 text-[0.8125rem] leading-normal text-foreground/70">
        {insight.whyItMatters}
      </p>
      <div className="mt-auto flex items-center justify-between gap-2 pt-0.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Layers className="size-[13px]" aria-hidden="true" />
          Evidence {insight.signalCount}
        </span>
        <OwnerBadge name={insight.suggestedOwner} mine={mine} />
      </div>
    </article>
  );
}

/** Equal shares while the cards fit; beyond that the next card peeks out. */
function cardWidthClassName(count: number): string {
  if (count <= 2) {
    return "basis-[calc((100%_-_12px)/2)] @min-[1000px]:basis-[calc((100%_-_24px)/3)]";
  }

  return count === 3
    ? "basis-[calc((100%_-_24px)/2.3)] @min-[1000px]:basis-[calc((100%_-_24px)/3)]"
    : "basis-[calc((100%_-_24px)/2.3)] @min-[1000px]:basis-[calc((100%_-_36px)/3.3)]";
}

const ARROW_CLASS_NAME =
  "absolute top-1/2 z-[2] -mt-[19px] inline-flex size-[38px] items-center justify-center rounded-full border border-foreground/20 bg-card text-foreground shadow-[0_6px_16px_-8px_rgba(0,0,0,0.35)] transition-[background-color,border-color,color,opacity,visibility] duration-200 hover:border-foreground hover:bg-foreground hover:text-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

/** The open type's cards in one row; arrows and edge fades follow the measured overflow. */
function InsightStrip({
  items,
  document,
  heading,
  onOpen,
}: {
  items: OverviewInsight[];
  document: OverviewDocument;
  heading: "h3" | "h4";
  onOpen: (id: string) => void;
}) {
  const stripRef = React.useRef<HTMLDivElement>(null);
  const edges = useStripEdges(stripRef);
  const widthClassName = cardWidthClassName(items.length);

  const slide = (direction: 1 | -1) => {
    const strip = stripRef.current;

    strip?.scrollBy({
      left: direction * Math.round(strip.clientWidth * 0.8),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return (
    <div className="relative overflow-hidden rounded-b-[14px] border bg-muted py-3">
      <div
        ref={stripRef}
        className="relative flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((insight) => (
          <InsightCard
            key={insight.id}
            insight={insight}
            mine={isViewerItem(document, insight)}
            heading={heading}
            onOpen={onOpen}
            className={cn("flex-none snap-start", widthClassName)}
          />
        ))}
      </div>
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-[88px] bg-gradient-to-r from-muted from-20% to-transparent transition-opacity duration-200",
          edges.canPrev ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-[120px] bg-gradient-to-l from-muted from-20% to-transparent transition-opacity duration-200",
          edges.canNext ? "opacity-100" : "opacity-0",
        )}
      />
      <button
        type="button"
        aria-label="Previous cards"
        onClick={() => slide(-1)}
        className={cn(
          ARROW_CLASS_NAME,
          "left-3.5",
          edges.canPrev ? "visible opacity-100" : "invisible opacity-0",
        )}
      >
        <ChevronLeft strokeWidth={2.2} className="size-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={`${edges.hiddenAfter} more to the right`}
        onClick={() => slide(1)}
        className={cn(
          ARROW_CLASS_NAME,
          "right-3.5",
          edges.canNext ? "visible opacity-100" : "invisible opacity-0",
        )}
      >
        <ChevronRight strokeWidth={2.2} className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/** Whether the deck is too narrow for tiles in a row; false on the server. */
function useStackedDeck(deck: React.RefObject<HTMLElement | null>): boolean {
  const [stacked, setStacked] = React.useState(false);

  React.useEffect(() => {
    const element = deck.current;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(([entry]) =>
      setStacked(entry.contentRect.width < STACK_BELOW),
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [deck]);

  return stacked;
}

// Snapping and fractional widths leave a pixel or two at either end.
const EDGE_TOLERANCE = 2;
const NO_EDGES = { canPrev: false, canNext: false, hiddenAfter: 0 };

/** Which ends of the strip hide more cards, measured on scroll and resize. */
function useStripEdges(strip: React.RefObject<HTMLDivElement | null>) {
  const [edges, setEdges] = React.useState(NO_EDGES);

  React.useEffect(() => {
    const element = strip.current;

    if (!element) {
      return;
    }

    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const visibleEnd = element.scrollLeft + element.clientWidth;
        const canNext = element.scrollWidth - visibleEnd > EDGE_TOLERANCE;
        const next = {
          canPrev: element.scrollLeft > EDGE_TOLERANCE,
          canNext,
          hiddenAfter: canNext
            ? [...element.children].filter((card) => {
                const node = card as HTMLElement;

                return node.offsetLeft + node.offsetWidth > visibleEnd + EDGE_TOLERANCE;
              }).length
            : 0,
        };

        setEdges((previous) =>
          previous.canPrev === next.canPrev &&
          previous.canNext === next.canNext &&
          previous.hiddenAfter === next.hiddenAfter
            ? previous
            : next,
        );
      });
    };

    measure();
    element.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [strip]);

  return edges;
}
